import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { noop } from 'lodash'
import { NEXT_GEN_APP_URL } from 'utils/AllURLs'
import { fetchUnreadNotificationCount, markAllAsReadForContact } from './utils'

const DEFAULT_CONTEXT = {
  unreadNotifications: 0,
  refetch: noop,
  markAllAsReadForContact: noop,
  setUnreadNotificationCount: noop,
}

const UnreadNotificationsContext = React.createContext(DEFAULT_CONTEXT)

export const Consumer = UnreadNotificationsContext.Consumer
const Provider = UnreadNotificationsContext.Provider

export const UnreadNotificationsProvider = ({
  refetchIntervalSeconds = 30,
  children = null,
}) => {
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0)
  const refetchIntervalMs = useMemo(
    () => refetchIntervalSeconds * 1000,
    [refetchIntervalSeconds],
  )

  const updateUnreadNotificationCountCallback = useCallback(async () => {
    setUnreadNotificationCount(await fetchUnreadNotificationCount())
  }, [])

  const markAllAsReadForContactCallback = useCallback(
    async contactId => {
      // Invoke the API to mark all notifications as read for the given contact
      await markAllAsReadForContact(contactId)

      // Refetch the notification count
      await updateUnreadNotificationCountCallback()
    },
    [updateUnreadNotificationCountCallback],
  )

  const setUnreadNotificationCountCallback = useCallback(
    count => setUnreadNotificationCount(count),
    [],
  )

  /**
   * An effect to refetch the notification count every `refetchIntervalSeconds` seconds
   */
  useEffect(() => {
    const timerId = setInterval(() => {
      updateUnreadNotificationCountCallback()
    }, refetchIntervalMs)

    // Fetch the first time
    updateUnreadNotificationCountCallback()

    return () => {
      clearInterval(timerId)
    }
  }, [refetchIntervalMs, updateUnreadNotificationCountCallback])

  /**
   * An effect to listen to notification-related messages from
   * the next-gen app and refetch the notification count
   */
  useEffect(() => {
    const messageListener = event => {
      if (event.origin !== NEXT_GEN_APP_URL) return
      if (event.data.type === 'notifications') {
        const { name, count } = event.data.payload
        if (name === 'unread-count') {
          setUnreadNotificationCount(count)
        }
      }
    }

    window.addEventListener('message', messageListener)

    return () => {
      window.removeEventListener('message', messageListener)
    }
  }, [updateUnreadNotificationCountCallback])

  /**
   * If the `unreadNotificationCount` state changes, notify any react-native
   * clients.
   */
  useEffect(() => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage({
        type: 'notifications',
        payload: {
          name: 'unread-count',
          count: unreadNotificationCount,
        },
      })
    }
  }, [unreadNotificationCount])

  const contextValue = useMemo(
    () => ({
      unreadNotifications: unreadNotificationCount,
      refetch: updateUnreadNotificationCountCallback,
      markAllAsReadForContact: markAllAsReadForContactCallback,
      setUnreadNotificationCount: setUnreadNotificationCountCallback,
    }),
    [
      markAllAsReadForContactCallback,
      setUnreadNotificationCountCallback,
      unreadNotificationCount,
      updateUnreadNotificationCountCallback,
    ],
  )

  return <Provider value={contextValue}>{children}</Provider>
}

export const useUnreadNotificationsContext = () =>
  useContext(UnreadNotificationsContext)

export const useUnreadNotificationsCount = () => {
  const { unreadNotifications } = useUnreadNotificationsContext()
  return unreadNotifications
}
